import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Modal, message } from "antd";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { getGoogleMapKey } from "@/services/app/global/google";
import { updatePropertyGeolocation } from "@/services/app/tenant/customer/customer";

// Must match every other loader on the dashboard — the @react-google-maps/api
// loader is global and the first load wins (see RouteMap).
const libraries: ("places")[] = ["places"];

const containerStyle = { width: "100%", height: "360px" };

// Continental-US fallback when we have neither stored coords nor a geocode hit.
const US_CENTER = { lat: 39.5, lng: -98.35 };

export interface PinAddressModalProps {
  open: boolean;
  /** The dispatch's job-site property, with customer_property_geolocation when one exists. */
  property?: API.CustomerProperty;
  onCancel: () => void;
  /** Called after a successful save so the caller can refresh the dispatch. */
  onSaved: () => void;
}

const formatAddress = (p?: API.CustomerProperty): string => {
  if (!p) return "";
  let addr = `${p.streetNumber || ""} ${p.streetName || ""}`.trim();
  if (p.streetDirection) addr += ` ${p.streetDirection}`;
  if (p.city) addr += `, ${p.city}`;
  if (p.state) addr += `, ${p.state}`;
  if (p.zip) addr += ` ${p.zip}`;
  return addr;
};

interface PinMapProps {
  googleKey: string;
  property: API.CustomerProperty;
  position: { lat: number; lng: number } | null;
  setPosition: (pos: { lat: number; lng: number }) => void;
}

const PinMap: React.FC<PinMapProps> = ({ googleKey, property, position, setPosition }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: googleKey,
    libraries,
    preventGoogleFontsLoading: true,
  });
  const [center, setCenter] = useState<{ lat: number; lng: number }>(position ?? US_CENTER);
  const geocodedRef = useRef(false);

  // No stored coords — geocode the address text once, just to center the map
  // near the right neighborhood. The office still places the pin by hand.
  useEffect(() => {
    if (!isLoaded || position || geocodedRef.current) return;
    geocodedRef.current = true;
    const text = formatAddress(property);
    if (!text) return;
    new google.maps.Geocoder().geocode({ address: text }, (results, status) => {
      const loc = results?.[0]?.geometry?.location;
      if (status === "OK" && loc) {
        setCenter({ lat: loc.lat(), lng: loc.lng() });
      }
    });
  }, [isLoaded, position, property]);

  const place = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (e.latLng) setPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    },
    [setPosition],
  );

  if (!isLoaded) {
    return <div style={{ textAlign: "center", padding: 24, color: "#999" }}>Loading map...</div>;
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={position ?? center}
      zoom={position || center !== US_CENTER ? 17 : 4}
      onClick={place}
      options={{ streetViewControl: false, mapTypeControl: true, fullscreenControl: false }}
    >
      {position && <Marker position={position} draggable onDragEnd={place} title="Drag to the exact location" />}
    </GoogleMap>
  );
};

/**
 * Verify-location modal for a property whose stored geolocation is only the
 * geocoder's nearest guess (accuracy=approximate) or missing entirely. The
 * office drops/drags a marker on the actual house; saving stores the pin with
 * accuracy=manual, which automated re-syncs never overwrite.
 */
const PinAddressModal: React.FC<PinAddressModalProps> = ({ open, property, onCancel, onSaved }) => {
  const [googleKey, setGoogleKey] = useState<string>("");
  const [position, setPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && !googleKey) {
      getGoogleMapKey()
        .then((res) => res?.key && setGoogleKey(res.key))
        .catch(() => message.error("Failed to load Google Maps key"));
    }
  }, [open, googleKey]);

  // Re-seed the marker from the stored (approximate) coords each time the modal opens.
  useEffect(() => {
    if (!open) return;
    const geo = property?.customer_property_geolocation;
    const lat = geo?.lat != null ? Number(geo.lat) : NaN;
    const lng = geo?.lng != null ? Number(geo.lng) : NaN;
    setPosition(!isNaN(lat) && !isNaN(lng) ? { lat, lng } : null);
  }, [open, property]);

  const handleSave = async () => {
    if (!property?.id || !position) return;
    setSaving(true);
    try {
      const res = await updatePropertyGeolocation({
        property_id: property.id,
        lat: position.lat,
        lng: position.lng,
      });
      if (res?.success) {
        message.success("Location pinned successfully");
        onSaved();
      } else {
        message.error(res?.message || "Failed to save location");
      }
    } catch (e: any) {
      message.error(e?.data?.message || "Failed to save location");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Pin Property Location"
      open={open}
      onCancel={onCancel}
      onOk={handleSave}
      okText="Save Pin"
      okButtonProps={{ disabled: !position }}
      confirmLoading={saving}
      width={640}
      destroyOnHidden
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message={formatAddress(property) || "No address on file"}
        description="The automated geocode could not verify this address, so the current pin is only the map service's nearest guess. Click the map or drag the marker to the actual property, then save."
      />
      {googleKey && property ? (
        <PinMap googleKey={googleKey} property={property} position={position} setPosition={setPosition} />
      ) : (
        <div style={{ textAlign: "center", padding: 24, color: "#999" }}>Loading map...</div>
      )}
    </Modal>
  );
};

export default PinAddressModal;
